import FloatingTVLIcon from "@/assets/icons/floating-apy.svg"
import FloatingBorrowIcon from "@/assets/icons/floating-borrow.svg"
import FloatingSupplyIcon from "@/assets/icons/floating-supply.svg"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import {
  DEFAULT_STE_SYMBOL,
  INITIAL_REL_PRICE,
  NULL_PRICE,
  PriceKey,
  renecPriceDataAtom,
  solanaTokenOraclePricesAtom,
} from "@/atoms/priceState"
import { selectedPoolAtom } from "@/atoms/shared"
import { supplyToEarnRewardAtom } from "@/atoms/supplyToEarnState"
import useIsMobile from "@/hooks/useMobileDevice"
import useTrans from "@/hooks/useTrans"
import useWalletConnect from "@/hooks/useWalletConnect"
import { SKELETON_VALUE } from "@/utils/constants"
import { formatPercent, formatUsd } from "@/utils/numberFormatter"
import { getSTERewardInfo } from "@/utils/supply-to-earn.utils"
import { Grid, Stack, Typography } from "@mui/material"
import { REL } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"
import "dayjs/locale/vi"
import { useAtom, useAtomValue } from "jotai"
import { useMemo } from "react"
import OverviewStatItem from "./OverviewStatItem"

const TotalsOverview = () => {
  const trans = useTrans()
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const [selectedPool] = useAtom(selectedPoolAtom)
  const supplyToEarnRewards = useAtomValue(supplyToEarnRewardAtom)
  const renecPriceData = useAtomValue(renecPriceDataAtom)
  const solanaTokenOraclePrices = useAtomValue(solanaTokenOraclePricesAtom)
  const { isRenecChain } = useWalletConnect()
  const isMobileDevice = useIsMobile()

  const usedObligation = selectedObligation ?? {
    totalSupplyValue: new BigNumber(0),
    totalBorrowValue: new BigNumber(0),
    borrowLimit: new BigNumber(0),
    liquidationThreshold: new BigNumber(0),
    borrowOverSupply: new BigNumber(0),
    borrowLimitOverSupply: new BigNumber(0),
    liquidationThresholdFactor: new BigNumber(0),
    weightedTotalBorrowValue: new BigNumber(0),
    weightedBorrowUtilization: new BigNumber(0),
  }

  const totalSupplyUSD =
    selectedObligation?.deposits.reduce((arr, r) => r.amountUsd.plus(arr), BigNumber(0)) ?? new BigNumber(0)

  const totalSupplyWithSteAPYInUSD =
    selectedObligation?.deposits.reduce((acc, item) => {
      const steReward = supplyToEarnRewards.find((ste) => ste.reserve === item.reserveAddress)
      const reserve = selectedPool?.reserves.find((r) => r.address === item?.reserveAddress)
      const baseSupplyAPY = reserve?.supplyAPYInterest ?? BigNumber(0)
      const baseSupplyVolume = item?.amountUsd ?? BigNumber(0)

      if (!item || !reserve) return acc

      const steRewardSymbol = steReward?.symbol ?? DEFAULT_STE_SYMBOL
      const steRewardOraclePrice =
        solanaTokenOraclePrices && steReward ? solanaTokenOraclePrices[steReward.address]?.spotPrice : 0
      const steRewardTwapFeedPrice = renecPriceData[steRewardSymbol as PriceKey] ?? item.price.toNumber()
      const lastResortRewardPrice = steRewardSymbol === REL ? INITIAL_REL_PRICE : NULL_PRICE
      const steRewardPrice =
        steRewardOraclePrice && steRewardOraclePrice > 0
          ? steRewardOraclePrice
          : isRenecChain
            ? steRewardTwapFeedPrice
            : lastResortRewardPrice

      const { steAPYOnToken, shouldDisplaySteAPY: shouldDisplayApy } = getSTERewardInfo(item, steRewardPrice, steReward)
      if (shouldDisplayApy) {
        const totalSupplyAPY = baseSupplyAPY.plus(steAPYOnToken)
        return baseSupplyVolume.multipliedBy(totalSupplyAPY).plus(acc)
      }

      return baseSupplyVolume.multipliedBy(baseSupplyAPY).plus(acc)
    }, BigNumber(0)) ?? new BigNumber(0)

  const totalBorrowWithAPYUSD =
    selectedObligation?.borrows.reduce((arr, reserve) => {
      const borrowAPY =
        selectedPool?.reserves.filter((r) => reserve.reserveAddress === r.address)[0]?.borrowAPRInterest ?? BigNumber(0)
      return reserve.amountUsd.multipliedBy(borrowAPY).plus(arr)
    }, BigNumber(0)) ?? new BigNumber(0)

  const netAPY = totalSupplyWithSteAPYInUSD.minus(totalBorrowWithAPYUSD).dividedBy(totalSupplyUSD)

  const statColor = useMemo(() => {
    if (!netAPY.isNaN()) {
      return netAPY.isGreaterThan(0) ? "text-primary" : netAPY.isLessThan(0) ? "text-red-600" : "text-neutral-1000"
    }
  }, [netAPY])

  const totalSupplyValueDisplay = useMemo(() => {
    return !selectedObligation
      ? SKELETON_VALUE
      : usedObligation.totalSupplyValue.isZero()
        ? "0"
        : formatUsd(usedObligation.totalSupplyValue)
  }, [selectedObligation, usedObligation.totalSupplyValue])

  const totalBorrowValueDisplay = useMemo(() => {
    return !selectedObligation
      ? SKELETON_VALUE
      : usedObligation.totalBorrowValue.isZero()
        ? "0"
        : formatUsd(usedObligation.totalBorrowValue)
  }, [selectedObligation, usedObligation.totalBorrowValue])

  const netAPYDisplay = useMemo(() => {
    return !selectedObligation ? SKELETON_VALUE : netAPY.isNaN() ? "0" : formatPercent(netAPY.isNaN() ? 0 : netAPY)
  }, [selectedObligation, netAPY])

  if (
    isMobileDevice &&
    !selectedObligation &&
    usedObligation.totalSupplyValue.isZero() &&
    usedObligation.totalBorrowValue.isZero() &&
    netAPY.isNaN()
  ) {
    return <></>
  }

  return (
    <Stack data-testid='my-account-totals-overview' className='bg-white py-4 px-5 rounded-lg h-full' gap={2}>
      <Typography className='font-bold text-xl'>{trans.dashboard.hero.my_account}</Typography>
      <Grid container spacing={2} className='h-full'>
        <Grid item xs={12} sm={4} md={4}>
          <OverviewStatItem
            icon={FloatingBorrowIcon}
            title={trans.dashboard.hero.total_supply}
            stat={totalSupplyValueDisplay}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <OverviewStatItem
            icon={FloatingSupplyIcon}
            title={trans.dashboard.hero.total_borrow}
            stat={totalBorrowValueDisplay}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={4}>
          <OverviewStatItem
            icon={FloatingTVLIcon}
            title={trans.dashboard.hero.net_apy}
            stat={netAPYDisplay}
            statColor={statColor}
          />
        </Grid>
      </Grid>
    </Stack>
  )
}

export default TotalsOverview
